import {
  type BarcodeBar,
  generateBarcodeBars,
} from 'features/moveMoney/screens/depositCash/BarcodeScreen/utils';
import { useEffect, useState } from 'react';
import { isWeb } from 'shared/constants/platform';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

interface BarcodeData {
  barcodeId: string;
  gdBarcodeIdExpiration: string;
}

const BARCODE_VIEWBOX_HEIGHT = 150;
const BARCODE_HORIZONTAL_PADDING = 20;

interface ViewBox {
  height: number;
  minX: number;
  width: number;
}

interface UseBarcodeVisualResult {
  barcodeBars: BarcodeBar[];
  barcodeViewBox: ViewBox;
}

const INITIAL_BARCODE_VIEWBOX: ViewBox = {
  height: BARCODE_VIEWBOX_HEIGHT,
  minX: 0,
  width: 740,
};

export const useBarcodeVisual = (
  barcodeData: BarcodeData | null,
): UseBarcodeVisualResult => {
  const [barcodeBars, setBarcodeBars] = useState<BarcodeBar[]>([]);
  const [barcodeViewBox, setBarcodeViewBox] = useState(INITIAL_BARCODE_VIEWBOX);

  useEffect(() => {
    if (isWeb || !barcodeData?.barcodeId) {
      return;
    }

    const generateBarcodeVisual = () => {
      try {
        const bars = generateBarcodeBars(barcodeData.barcodeId);
        if (bars.length > 0) {
          const minX = Math.min(...bars.map((bar) => bar.x));
          const maxX = Math.max(...bars.map((bar) => bar.x + bar.width));
          const actualWidth = maxX - minX;
          const normalizedBars = bars.map((bar) => ({
            ...bar,
            x: bar.x - minX + BARCODE_HORIZONTAL_PADDING,
          }));
          setBarcodeViewBox({
            height: BARCODE_VIEWBOX_HEIGHT,
            minX: 0,
            width: actualWidth + BARCODE_HORIZONTAL_PADDING * 2,
          });
          setBarcodeBars(normalizedBars);
        } else {
          setBarcodeBars(bars);
        }
      } catch (error) {
        logErrorWithContext(error, {
          featureArea: FeatureArea.PAYMENTS,
          responsibleTeam: ResponsibleTeam.PAYMENTS_TRANSFER,
          severity: ErrorSeverity.MEDIUM,
          additionalContext: {
            event: 'generateBarcodeVisual',
          },
        });
      }
    };

    generateBarcodeVisual();
  }, [barcodeData?.barcodeId]);

  return {
    barcodeBars,
    barcodeViewBox,
  };
};
